import React from 'react'
import classNames from 'classnames'

import Typography from '@material-ui/core/Typography'
import { withStyles, createStyles, WithStyles, Theme } from '@material-ui/core'

interface Props {
  value: number
  min: number
  max: number
  textAlign?: 'left' | 'right' | 'center'
  label?: React.ReactChild
  size?: 'small' | 'medium' | 'large'
  color?: 'default' | 'primary' | 'secondary'
  className?: string
}

const heights = {
  small: 22,
  medium: 28,
  large: 34,
}

const styles = (theme: Theme) => createStyles({
  root: {
    borderRadius: theme.borderRadius,
    backgroundColor: theme.palette.grey['100'],
    position: 'relative',
    overflow: 'hidden',
  },
  mask: {
    position: 'absolute',
    zIndex: 1,
    left: 0,
  },
  labelContainer: {
    paddingLeft: theme.spacing(),
    position: 'absolute',
    width: '100%',
    zIndex: 2,
  },
})

type InjectedProps = Props & WithStyles<typeof styles, true>

const ProgressBar: React.SFC<InjectedProps> = ({
  value,
  size,
  max,
  label,
  textAlign,
  color,
  classes,
  className,
  theme,
}) => {
  const maskWidth = value > 0 ? (value / max * 100) : 0
  const fontVariant = size === 'small' ? 'caption' : 'body1'
  const height = heights[size || 'medium']

  const colors = {
    'default': {
      textColor: theme!.palette.primary.contrastText,
      maskColor: theme!.palette.grey['200'],
      background: theme!.palette.grey['100'],
    },
    'primary': {
      textColor: theme!.palette.tertiary.contrastText,
      maskColor: `linear-gradient(90deg, ${theme!.palette.tertiary.main}, ${theme!.palette.primary.main} 200%)`,
      background: theme!.palette.accent.main,
    },
    'secondary': {
      textColor: theme!.palette.tertiary.contrastText,
      maskColor: theme!.palette.tertiary.main,
      background: theme!.palette.grey['100'],
    },
  }

  const style = colors[color || 'default']

  return (
    <div className={classNames(className, classes.root)}>
      <div className={classes.labelContainer}>
        <Typography
          align={textAlign}
          variant={fontVariant}
          style={{
            lineHeight: `${height}px`,
            color: style.textColor,
          }}
        >
          {label}
        </Typography>
      </div>
      <div
        className={classes.mask}
        style={{
          background: style.maskColor,
          width: `${maskWidth}%`,
          height,
        }}
      >
        &nbsp;
      </div>
      <div style={{ height, background: style.background }}>
        &nbsp;
      </div>
    </div>
  )
}

export default withStyles(styles, { withTheme: true })(ProgressBar)
